/**
 * Полноэкранная фотогалерея товара с клавиатурой и свайпами.
 * Список снимков берётся из миниатюр; выбранное фото открывается первым.
 */
export function initGallery( app ) {

    initThumbnails( app );

    const main = app.querySelector('[data-gallery-main]');

    if( ! main )
        return;

    const dialog = document.createElement('dialog');

    dialog.className = 'photo-viewer';
    dialog.setAttribute( 'aria-label', 'Фотографии товара' );
    dialog.innerHTML = '<button type="button" class="photo-viewer-close" aria-label="Закрыть">×</button><button type="button" class="photo-viewer-prev" aria-label="Предыдущее фото">‹</button><img alt=""><button type="button" class="photo-viewer-next" aria-label="Следующее фото">›</button><output aria-live="polite"></output>';
    document.body.append( dialog );

    const photo   = dialog.querySelector('img');
    const counter = dialog.querySelector('output');
    const prev    = dialog.querySelector('.photo-viewer-prev');
    const next    = dialog.querySelector('.photo-viewer-next');

    let images   = [];
    let index    = 0;
    let overflow = '';
    let touchX   = null;

    function show( step = 0 ) {

        index = ( index + step + images.length ) % images.length;

        photo.src           = images[ index ];
        photo.alt           = main.alt;
        counter.textContent = `${ index + 1 } / ${ images.length }`;
        prev.hidden         = images.length < 2;
        next.hidden         = images.length < 2;
    }

    function open() {

        images = [ ...new Set( Array.from( app.querySelectorAll('[data-gallery-image]'), el => el.dataset.galleryImage ) ) ];

        if( ! images.includes( main.src ) )
            images.push( main.src );

        index = images.indexOf( main.src );

        show();

        overflow                     = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.classList.add('photo-viewer-open');

        dialog.showModal();
    }

    main.tabIndex = 0;
    main.setAttribute( 'role', 'button' );
    main.setAttribute( 'aria-label', 'Открыть фото на весь экран' );
    main.setAttribute( 'aria-haspopup', 'dialog' );
    main.addEventListener( 'click', open );
    main.addEventListener( 'keydown', ( event ) => {

        if( event.key === 'Enter' || event.key === ' ' ) {
            event.preventDefault();
            open();
        }
    } );

    dialog.querySelector('.photo-viewer-close').addEventListener( 'click', () => dialog.close() );
    prev.addEventListener( 'click', () => show( -1 ) );
    next.addEventListener( 'click', () => show( 1 ) );
    dialog.addEventListener( 'click', ( event ) => {

        if( event.target === dialog )
            dialog.close();
    } );
    dialog.addEventListener( 'keydown', ( event ) => {

        if( event.key === 'ArrowLeft' || event.key === 'ArrowRight' ) {
            event.preventDefault();
            show( event.key === 'ArrowLeft' ? -1 : 1 );
        }
    } );
    dialog.addEventListener( 'touchstart', ( event ) => {
        touchX = event.touches.length === 1 ? event.changedTouches[0].clientX : null;
    }, { passive: true } );
    dialog.addEventListener( 'touchend', ( event ) => {

        if( touchX !== null ) {

            const distance = event.changedTouches[0].clientX - touchX;

            if( Math.abs( distance ) > 50 )
                show( distance > 0 ? -1 : 1 );
        }

        touchX = null;
    }, { passive: true } );
    dialog.addEventListener( 'close', () => {
        document.documentElement.classList.remove('photo-viewer-open');
        document.body.style.overflow = overflow;
        main.focus( { preventScroll: true } );
    } );
}

/**
 * Лента миниатюр сохраняет нативный свайп, добавляет стрелки и drag мышью.
 * После перетаскивания клик подавляется, чтобы не менять фото случайно.
 */
function initThumbnails( app ) {

    const strip = app.querySelector('[data-gallery-thumbs]');

    if( ! strip )
        return;

    const wrapper = document.createElement('div');
    const prev    = document.createElement('button');
    const next    = document.createElement('button');

    wrapper.className = 'gallery-strip';
    strip.before( wrapper );
    wrapper.append( prev, strip, next );

    [ prev, next ].forEach( ( button ) => {
        button.type      = 'button';
        button.className = 'gallery-strip-arrow';
    } );

    prev.textContent = '‹';
    next.textContent = '›';
    prev.setAttribute( 'aria-label', 'Прокрутить фотографии назад' );
    next.setAttribute( 'aria-label', 'Прокрутить фотографии вперёд' );

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    function update() {
        const max = strip.scrollWidth - strip.clientWidth;

        wrapper.classList.toggle( 'has-overflow', max > 1 );
        prev.disabled = strip.scrollLeft <= 1;
        next.disabled = strip.scrollLeft >= max - 1;
    }

    function move( direction ) {
        strip.scrollBy( { left: direction * strip.clientWidth * 0.75, behavior } );
    }

    prev.addEventListener( 'click', () => move( -1 ) );
    next.addEventListener( 'click', () => move( 1 ) );
    strip.addEventListener( 'scroll', update, { passive: true } );
    new ResizeObserver( update ).observe( strip );

    let drag = null;
    let suppressClick = false;

    strip.addEventListener( 'dragstart', ( event ) => event.preventDefault() );
    strip.addEventListener( 'pointerdown', ( event ) => {

        if( event.pointerType !== 'mouse' || event.button !== 0 )
            return;

        suppressClick = false;
        drag = { id: event.pointerId, x: event.clientX, left: strip.scrollLeft, moved: false };
    } );
    strip.addEventListener( 'pointermove', ( event ) => {

        if( ! drag || event.pointerId !== drag.id )
            return;

        const distance = event.clientX - drag.x;

        if( ! drag.moved && Math.abs( distance ) > 5 ) {
            drag.moved = true;
            strip.setPointerCapture( drag.id );
            strip.classList.add('is-dragging');
        }

        if( drag.moved ) {
            event.preventDefault();
            strip.scrollLeft = drag.left - distance;
        }
    } );

    function endDrag() {

        if( ! drag )
            return;

        suppressClick = drag.moved;

        if( strip.hasPointerCapture( drag.id ) )
            strip.releasePointerCapture( drag.id );

        strip.classList.remove('is-dragging');
        drag = null;
    }

    window.addEventListener( 'pointerup', endDrag );
    strip.addEventListener( 'pointercancel', endDrag );
    strip.addEventListener( 'click', ( event ) => {

        if( suppressClick ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            suppressClick = false;
            return;
        }

        const thumb = event.target.closest('.gallery-thumb');

        if( thumb ) {
            const left = thumb.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
            const right = left + thumb.offsetWidth;

            if( left < strip.scrollLeft )
                strip.scrollTo( { left, behavior } );
            else if( right > strip.scrollLeft + strip.clientWidth )
                strip.scrollTo( { left: right - strip.clientWidth, behavior } );
        }
    }, true );

    update();
}
